'use client'

import { useContext } from 'react'

import { Cell, Image, Text, Label, Card, Icon, Spacer } from '@vinted/web-ui'
import { Home24, Printer16 } from '@vinted/monochrome-icons'

import { GetShippingLabelContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import {
  DropOffTypeKey,
  DropOffsPointType,
  DropOffType,
  PrinterRequirement,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

const ShippingMethod = () => {
  const translate = useTranslate('conversation.get_shipping_label.modal')
  const {
    state: {
      labelOptions: { labelOptions, labelOptionsRewrite },
      dropOffPointTypeSelection: { selectedDropOffPointType },
      dropOffTypeSelection: { selectedDropOffTypeKey },
    },
  } = useContext(GetShippingLabelContext)

  const effectiveLabelOptions = labelOptionsRewrite || labelOptions

  const labelOptionsDropOffs = effectiveLabelOptions?.dropOffs
  const isHomePickupSelected =
    selectedDropOffPointType === DropOffsPointType.Home ||
    selectedDropOffTypeKey === DropOffType.Home

  const getPrinterRequirementForHome = () => {
    const homeDropOff = labelOptionsRewrite?.dropOffs?.find(
      dropOff => dropOff.type === DropOffType.Home,
    )
    const printerOption = homeDropOff?.options?.find(option => option.type === DropOffType.Home)

    return printerOption?.printerRequirement || null
  }

  const oldLabelOptionDropOffType = labelOptions?.dropOffTypes?.[0]?.key

  const hasShippingMethodData =
    oldLabelOptionDropOffType === DropOffTypeKey.Home2Home ||
    oldLabelOptionDropOffType === DropOffTypeKey.ContactlessDropoff

  const shouldShowShippingData =
    hasShippingMethodData ||
    isHomePickupSelected ||
    (labelOptionsDropOffs &&
      labelOptionsDropOffs.length === 1 &&
      labelOptionsDropOffs[0]?.type === DropOffType.Home)

  if (!shouldShowShippingData) return null

  const renderPrinterText = () => {
    const printerRequirement = getPrinterRequirementForHome()

    switch (printerRequirement) {
      case PrinterRequirement.PrinterRequired:
        return translate('home_shipping_method.printer_requirement')
      case PrinterRequirement.PrinterOptional:
        return translate('home_shipping_method.printer_optional')
      case PrinterRequirement.PrinterNotRequired:
        return translate('home_shipping_method.printer_not_required')
      default:
        return translate('home_shipping_method.printer_requirement')
    }
  }

  const renderIcon = () =>
    labelOptionsRewrite ? (
      <Icon name={Printer16} />
    ) : (
      <Image src={labelOptions?.dropOffTypes?.[0]?.dropOffTypeMeta?.printIcon?.url} size="small" />
    )

  const titleText = hasShippingMethodData
    ? translate('shipping_method.title')
    : translate('home_shipping_method.title')

  const bodyText = hasShippingMethodData
    ? translate('shipping_method.body')
    : translate('home_shipping_method.body')

  const printerRequirementText =
    labelOptions?.dropOffTypes?.[0]?.dropOffTypeMeta?.printIcon?.text || renderPrinterText()

  const renderPrinterRequirement = () => {
    if (hasShippingMethodData) {
      return (
        <div className="u-inline-flex">
          {renderIcon()}
          <Spacer orientation="vertical" />
          <Text as="h3" type="caption" text={printerRequirementText} />
        </div>
      )
    }

    return (
      <div className="u-flexbox">
        {renderIcon()}
        <Spacer orientation="vertical" />
        <Text as="h3" type="caption" text={printerRequirementText} />
      </div>
    )
  }

  return (
    <>
      <Label type="stacked" text={translate('shipping_method.label')} />
      <Cell testId="generate-label-modal-shipping-method">
        <Card>
          <Cell theme="transparent">
            <div className="u-flexbox u-flex-direction-column">
              <Icon name={Home24} />
              <Spacer />
              <Text as="h2" type="title" text={titleText} />
              <Text as="span" text={bodyText} />
            </div>
            <Spacer />
            {renderPrinterRequirement()}
          </Cell>
        </Card>
      </Cell>
    </>
  )
}

export default ShippingMethod
